import type { LocationContent } from "@/components/location-page";
import { FEES, FIRM } from "@/lib/firm";

export const BRISBANE: LocationContent = {
  slug: "conveyancing-brisbane",
  name: "Brisbane",
  title: "Conveyancing in Brisbane",
  metaTitle: "Conveyancing Brisbane | Fixed Fee Solicitors",
  description: `Fixed fee solicitor conveyancing for Brisbane buyers and sellers. Sale ${FEES.sale}, purchase ${FEES.purchase} incl. GST. Free contract review. Call ${FIRM.phone}.`,
  lead: `Solicitor-led conveyancing for Brisbane property, at a fixed fee — ${FEES.sale} to sell and ${FEES.purchase} to buy, both including GST. Contract review before you sign is free.`,
  paragraphs: [
    "Queensland settlements are electronic through PEXA, so the office does not need to be around the corner from the property. We act for Brisbane buyers and sellers the same way we act on the coast: the contract is read before you sign it, the searches are read properly, and the same lawyer stays on the file through to settlement.",
    "Brisbane transactions raise their own issues — character and demolition control overlays in the inner suburbs, flood mapping across the river catchments, high-density body corporate schemes in the CBD and Fortitude Valley fringe, and a steady volume of deceased estate and family transfers as older housing stock changes hands.",
    "Because we are a law practice rather than a processing service, a caveat, a defective disclosure statement, a deceased registered owner or a contract dispute is dealt with in-house rather than referred out mid-transaction.",
  ],
  localNotes: [
    {
      t: "Flood and overland flow",
      d: "Brisbane City Council's flood awareness mapping and overland flow overlays affect insurance, finance and what can be rebuilt. We order the relevant searches and tell you what the mapping actually means for the lot, not just that it exists.",
    },
    {
      t: "Character and demolition control",
      d: "Pre-1947 houses in the traditional building character overlay cannot simply be demolished or reshaped. If your plan is to knock down, extend or raise and build under, this needs checking before the contract goes unconditional.",
    },
    {
      t: "High-density body corporate",
      d: "Inner-city apartment schemes carry large sinking fund obligations, defect histories and letting arrangements. We review the minutes, levy history and any special levy struck or foreshadowed before your cooling-off period ends.",
    },
    {
      t: "Deceased estates in the title",
      d: "Where the registered owner has died, the sale needs a transmission application and, usually, a grant of probate. We handle the estate work and the conveyance together rather than splitting it across two firms.",
    },
    {
      t: "Unapproved building work",
      d: "Enclosed carports, decks and under-house conversions without approval are common in older Brisbane suburbs. Council building records are searched, and the position is resolved before settlement rather than becoming the buyer's problem afterwards.",
    },
    {
      t: "Seller disclosure (Form 2)",
      d: "Since 1 August 2025 sellers must give a compliant disclosure statement and prescribed certificates before the buyer signs. Getting it wrong gives the buyer a right to terminate right up to settlement. We prepare it before the property is listed.",
    },
  ],
  suburbs: [
    "Brisbane CBD",
    "New Farm",
    "Paddington",
    "Bulimba",
    "Chermside",
    "Carindale",
    "Sunnybank",
    "Indooroopilly",
    "Aspley",
    "Sandgate",
    "Bracken Ridge",
    "Wynnum",
  ],
  faqs: [
    {
      q: "Do you need an office in Brisbane to act on a Brisbane property?",
      a: "No. Queensland settlements are conducted electronically in PEXA, and identity verification and signing are done digitally. Everything is handled by phone, email and video, and we confirm settlement to you and your agent the moment it goes through.",
    },
    {
      q: "How much is conveyancing in Brisbane?",
      a: `Our professional fee is fixed: ${FEES.sale} ${FEES.saleNote} for a sale and ${FEES.purchase} ${FEES.purchaseNote} for a purchase. Disbursements — title and council searches, PEXA and registration fees, and transfer duty — are separate and charged at cost.`,
    },
    {
      q: "Do you charge to review a contract before I sign?",
      a: "No. Contract review before signing is free, and if a contract we act on terminates under cooling-off, finance or building and pest, we do not charge a professional fee — only disbursements actually incurred.",
    },
    {
      q: "Can you act on a Brisbane apartment purchase?",
      a: "Yes. Community titles schemes are a large part of our work. We obtain the body corporate records and report on levies, the sinking fund forecast, insurance, by-laws, defects and any litigation before your cooling-off period expires.",
    },
  ],
};
